import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { useEbayOAuthMutation } from "@/hooks/ebay-mutation";
import {
  platforms_ebay_oauth_shopify_redirect,
  VITE_API_URL,
} from "@/constants";

// Consents started in the embedded Shopify app carry a state. Their merchants
// aren't signed in here, so they're sent back to the app to finish connecting
async function getShopifyRedirect(params: URLSearchParams) {
  const res = await fetch(
    `${VITE_API_URL}${platforms_ebay_oauth_shopify_redirect}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: params.get("code") || "",
        state: params.get("state") || "",
        error: params.get("error") || "",
        error_description: params.get("error_description") || "",
      }),
    },
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    throw new Error(data.msg || i18n.t("auth.returnToShopifyFailed"));
  }

  return data.url as string;
}

export const Route = createFileRoute("/auth/ebay/callback")({
  component: EbayCallbackComponent,
});

function EbayCallbackComponent() {
  const { t } = useTranslation();
  // Get URL parameters manually
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code") || "";
  const state = urlParams.get("state") || "";
  const error = urlParams.get("error") || "";
  const error_description = urlParams.get("error_description") || "";

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [hasProcessed, setHasProcessed] = useState(false);
  const ebayOAuthMutation = useEbayOAuthMutation();

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed) return;

    const handleOAuthCallback = async () => {
      setHasProcessed(true);

      if (state) {
        try {
          window.location.replace(await getShopifyRedirect(urlParams));
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : t("common.networkError");
          console.error("eBay OAuth Shopify redirect error:", error);
          setErrorMessage(errorMsg);
          setStatus("error");
        }
        return;
      }

      if (error) {
        const errorMsg = error_description || error;
        console.error("eBay OAuth error:", errorMsg);
        setErrorMessage(t("auth.ebay.connectionFailedWith", { error: errorMsg }));
        setStatus("error");
        return;
      }

      if (!code) {
        const errorMsg = t("auth.ebay.noCode");
        console.error("eBay OAuth error:", errorMsg);
        setErrorMessage(errorMsg);
        setStatus("error");
        return;
      }

      try {
        console.log("Processing OAuth code:", code);
        // Handle OAuth directly - much simpler and more reliable
        await ebayOAuthMutation.mutateAsync(code);
        setStatus("success");

        // Get the return URL from session storage, or default to dashboard
        const returnUrl =
          sessionStorage.getItem("ebay-oauth-return-url") || "/";
        sessionStorage.removeItem("ebay-oauth-return-url");

        // Redirect back to where the user came from
        setTimeout(() => {
          const redirectUrl = new URL(returnUrl, window.location.origin);
          redirectUrl.searchParams.set("platform", "ebay");
          redirectUrl.searchParams.set("message", "connected");
          window.location.href = redirectUrl.toString();
        }, 2000);
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : t("common.networkError");
        console.error("eBay OAuth network error:", error);
        setErrorMessage(errorMsg);
        setStatus("error");
      }
    };

    handleOAuthCallback();
  }, [code, state, error, error_description, hasProcessed]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("auth.ebay.connecting")}
          </h2>
          <p className="text-gray-600">
            {t("auth.ebay.pleaseWait")}
          </p>
          <p className="text-sm text-gray-500 mt-4">
            {t("auth.ebay.redirectWhenFinished")}
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-4">
          <div className="text-red-600 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("auth.connectionFailed")}
          </h2>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <p className="text-sm text-gray-500">
            {t("auth.ebay.tryAgain")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-4">
        <div className="text-green-600 mb-4">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t("auth.ebay.successTitle")}
        </h2>
        <p className="text-gray-600 mb-4">
          {t("auth.ebay.successBody")}
        </p>
        <p className="text-sm text-gray-500">
          {t("auth.ebay.redirecting")}
        </p>
      </div>
    </div>
  );
}
